<script setup>
import { computed, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import {
  getControlRoomReminderSeverity,
  isControlRoomReminderCritical,
} from 'dashboard/helper/controlRoomReminderHelper';

const props = defineProps({
  elapsedSeconds: {
    type: Number,
    default: 0,
  },
  controlRoomUrl: {
    type: String,
    default: '#',
  },
  hasControlRoomPhoneNumber: {
    type: Boolean,
    default: false,
  },
  controlRoomTooltip: {
    type: String,
    default: null,
  },
  isMuted: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['openControlRoom', 'muteAlert']);
const { t } = useI18n();

const CRITICAL_ALERT_INTERVAL = 2500;
const CRITICAL_ALERT_TONE = 'bell';
const CRITICAL_NUDGE_DURATION = 140;
const CRITICAL_DETAIL_INTERVAL = 5000;
const CRITICAL_DETAIL_COUNT = 16;
const criticalImage = '/assets/images/rage-comics-tear.gif';

const severity = computed(() =>
  getControlRoomReminderSeverity(props.elapsedSeconds)
);

const isCritical = computed(() =>
  isControlRoomReminderCritical(props.elapsedSeconds)
);

const severityStyles = computed(() => {
  const styles = {
    notice: {
      bannerClass:
        'border-n-amber-7/45 bg-gradient-to-r from-n-amber-5/35 via-n-amber-4/15 to-transparent text-n-amber-12',
      iconClass: 'text-n-amber-11',
    },
    watch: {
      bannerClass:
        'border-n-amber-8/55 bg-gradient-to-r from-n-amber-6/45 via-n-amber-5/20 to-transparent text-n-amber-12',
      iconClass: 'text-n-amber-11',
    },
    warning: {
      bannerClass:
        'border-n-ruby-7/50 bg-gradient-to-r from-n-ruby-5/35 via-n-ruby-4/20 to-transparent text-n-ruby-12 motion-safe:animate-pulse',
      iconClass: 'text-n-ruby-11',
    },
    critical: {
      bannerClass:
        'border-n-ruby-8/65 bg-gradient-to-r from-n-ruby-6/55 via-n-ruby-5/30 to-transparent text-n-ruby-12 ring-2 ring-n-ruby-8/40 shadow-lg motion-safe:animate-pulse',
      iconClass: 'text-n-ruby-11 motion-safe:animate-pulse',
      icon: 'i-lucide-volume-2',
    },
  };

  return {
    icon: 'i-lucide-triangle-alert',
    ...styles[severity.value],
  };
});

const criticalAlertAudio = ref(null);
const isCriticalNudgeActive = ref(false);
const criticalDetailIndex = ref(0);
let criticalAlertTimer = null;
let criticalNudgeFrame = null;
let criticalNudgeTimer = null;
let criticalDetailTimer = null;

const nudgeCriticalReminder = () => {
  isCriticalNudgeActive.value = false;
  cancelAnimationFrame(criticalNudgeFrame);

  criticalNudgeFrame = window.requestAnimationFrame(() => {
    isCriticalNudgeActive.value = true;
    clearTimeout(criticalNudgeTimer);
    criticalNudgeTimer = window.setTimeout(() => {
      isCriticalNudgeActive.value = false;
    }, CRITICAL_NUDGE_DURATION);
  });
};

const playCriticalAlert = async () => {
  if (props.isMuted) return;

  nudgeCriticalReminder();

  if (!criticalAlertAudio.value) {
    criticalAlertAudio.value = new Audio(
      `/audio/dashboard/${CRITICAL_ALERT_TONE}.mp3`
    );
    criticalAlertAudio.value.preload = 'auto';
  }

  try {
    criticalAlertAudio.value.pause();
    criticalAlertAudio.value.currentTime = 0;
    await criticalAlertAudio.value.play();
  } catch {
    // Browsers can block sound until the agent has interacted with the page.
  }
};

const stopCriticalAlert = () => {
  if (criticalAlertTimer) {
    clearInterval(criticalAlertTimer);
    criticalAlertTimer = null;
  }
  cancelAnimationFrame(criticalNudgeFrame);
  clearTimeout(criticalNudgeTimer);
  isCriticalNudgeActive.value = false;

  if (criticalAlertAudio.value) {
    criticalAlertAudio.value.pause();
    criticalAlertAudio.value.currentTime = 0;
  }
};

const startCriticalAlert = () => {
  if (criticalAlertTimer || props.isMuted) return;

  playCriticalAlert();
  criticalAlertTimer = window.setInterval(
    playCriticalAlert,
    CRITICAL_ALERT_INTERVAL
  );
};

const startCriticalDetailRotation = () => {
  if (criticalDetailTimer) return;

  criticalDetailIndex.value = 0;
  criticalDetailTimer = window.setInterval(() => {
    criticalDetailIndex.value =
      (criticalDetailIndex.value + 1) % CRITICAL_DETAIL_COUNT;
  }, CRITICAL_DETAIL_INTERVAL);
};

const stopCriticalDetailRotation = () => {
  if (criticalDetailTimer) {
    clearInterval(criticalDetailTimer);
    criticalDetailTimer = null;
  }
  criticalDetailIndex.value = 0;
};

watch(
  isCritical,
  shouldAlert => {
    if (shouldAlert) {
      startCriticalDetailRotation();
      return;
    }

    stopCriticalAlert();
    stopCriticalDetailRotation();
  },
  { immediate: true }
);

watch(
  [isCritical, () => props.isMuted],
  ([shouldAlert, isMuted]) => {
    if (shouldAlert && !isMuted) {
      startCriticalAlert();
      return;
    }

    stopCriticalAlert();
  },
  { immediate: true }
);

onUnmounted(() => {
  stopCriticalAlert();
  stopCriticalDetailRotation();
});

const actionTooltip = computed(() =>
  props.hasControlRoomPhoneNumber ? null : props.controlRoomTooltip
);

const reminderMessage = computed(() => {
  if (severity.value === 'critical') {
    return t('CONVERSATION.FOOTER.CONTROL_ROOM_REMINDER.CRITICAL');
  }

  if (severity.value === 'warning') {
    return t('CONVERSATION.FOOTER.CONTROL_ROOM_REMINDER.WARNING');
  }

  if (severity.value === 'watch') {
    return t('CONVERSATION.FOOTER.CONTROL_ROOM_REMINDER.WATCH');
  }

  return t('CONVERSATION.FOOTER.CONTROL_ROOM_REMINDER.NOTICE');
});

const criticalReminderDetail = computed(() =>
  isCritical.value
    ? t(
        `CONVERSATION.FOOTER.CONTROL_ROOM_REMINDER.CRITICAL_DETAIL_${criticalDetailIndex.value + 1}`
      )
    : null
);

const openActionLabel = computed(() =>
  isCritical.value
    ? t('CONVERSATION.FOOTER.CONTROL_ROOM_REMINDER.CRITICAL_OPEN_ACTION')
    : t('CONVERSATION.FOOTER.CONTROL_ROOM_REMINDER.OPEN_ACTION')
);

const openControlRoom = () => {
  if (!props.hasControlRoomPhoneNumber) return;

  stopCriticalAlert();
  window.open(props.controlRoomUrl, '_blank', 'noopener,noreferrer');
  emit('openControlRoom');
};

const muteAlert = () => {
  stopCriticalAlert();
  emit('muteAlert');
};
</script>

<template>
  <div
    class="flex items-center justify-between gap-3 px-3 py-2 mx-2 mb-2 rounded-lg border backdrop-blur-[2px] transition-transform duration-100"
    :class="[
      severityStyles.bannerClass,
      { 'motion-safe:-translate-x-1': isCriticalNudgeActive },
    ]"
  >
    <div class="flex items-start gap-2 min-w-0">
      <span
        class="mt-0.5 text-sm flex-shrink-0"
        :class="[severityStyles.icon, severityStyles.iconClass]"
      />
      <div class="min-w-0">
        <p class="m-0 text-xs font-semibold leading-5">
          {{ reminderMessage }}
        </p>
        <p
          v-if="criticalReminderDetail"
          class="m-0 text-[0.6875rem] leading-4 text-n-ruby-11"
        >
          {{ criticalReminderDetail }}
        </p>
      </div>
    </div>
    <img
      v-if="isCritical"
      :src="criticalImage"
      alt=""
      aria-hidden="true"
      class="pointer-events-none select-none flex-shrink-0 h-[2.2rem] w-auto object-contain opacity-80"
    />
    <div class="flex items-center gap-1 flex-shrink-0">
      <div
        v-if="isCritical && !isMuted"
        v-tooltip.top-end="
          t('CONVERSATION.FOOTER.CONTROL_ROOM_REMINDER.MUTE_ACTION')
        "
      >
        <NextButton
          icon="i-lucide-volume-off"
          slate
          ghost
          sm
          @click="muteAlert"
        />
      </div>
      <div v-tooltip.top-end="actionTooltip">
        <NextButton
          :label="openActionLabel"
          icon="i-lucide-heart-pulse"
          slate
          faded
          sm
          :disabled="!hasControlRoomPhoneNumber"
          @click="openControlRoom"
        />
      </div>
    </div>
  </div>
</template>
